// Zod DTOs for the `/v1/checkout` billing surface (P4-02, spec Phase-4,
// `docs/design/P4-payment-provider-abstraction.md` §4/§6/§7, LOCKED
// 2026-08-20). Mirrors `./roster.ts`'s header convention for this package.
//
// I-2 (server-set money): `checkoutRequestSchema` deliberately has NO
// `amountMinor`/`price` field of any kind, and is `.strict()` — a client
// that tampers the body with an extra pricing key gets a 422 zod rejection,
// never a discount (design doc §4.1, ticket How-to-test "Price is
// server-side"). The amount is looked up server-side from
// `apps/api/src/services/payments/catalog.ts`'s `BILLING_CATALOG`, never
// accepted from the wire.
import { z } from 'zod';
import { idSchema } from './primitives';

/** The two `ProviderId`s the frozen `PaymentProvider` contract supports
 * (`apps/api/src/services/payments/types.ts`) — kept in sync manually since
 * `packages/shared` has no Prisma/backend-only import boundary crossing
 * `apps/api/src/services/payments/types.ts` (that file lives in the Worker
 * app, not this package); a THIRD provider id is a contract change requiring
 * cto sign-off in both places at once (design doc §3's "closed union" note). */
export const providerIdSchema = z.enum(['stripe', 'sslcommerz']);
export type ProviderIdDto = z.infer<typeof providerIdSchema>;

/** ISO-4217, uppercase, exactly 3 letters — matches the `Subscription.currency`
 * DB `CHECK (length(currency) = 3)` (P4-01) at the wire boundary (design doc
 * §4.4: "zod enforces case/charset at the edge"). */
export const currencyCodeSchema = z
  .string()
  .trim()
  .length(3, 'currency must be a 3-letter ISO-4217 code')
  .regex(/^[A-Z]{3}$/, 'currency must be uppercase ISO-4217 (e.g. "BDT", "USD")');

// ─── POST /v1/checkout ──────────────────────────────────────────────────────

export const checkoutRequestSchema = z
  .object({
    /** feat/plans-package-unify: was `levelId` — checkout now targets a
     * `Package` (the DB-backed `/v1/plans` tier), not a `Level` directly. */
    packageId: idSchema,
    /** Explicit provider override (design doc §6 step 1) — honoured only if
     * enabled + supports the resolved currency; otherwise 422. */
    provider: providerIdSchema.optional(),
    /** Explicit currency (design doc §6 step 2) — validated shape only here;
     * whether the level actually sells in this currency is a catalogue
     * lookup (`resolveSku`), not a DTO concern. */
    currency: currencyCodeSchema.optional(),
  })
  .strict();
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const checkoutResponseSchema = z.object({
  /** Where the SPA sends the browser next (`window.location.assign`) — the
   * provider's hosted checkout page. Always `https://`. */
  redirectUrl: z.string().url(),
  provider: providerIdSchema,
  /** The provider's own id for this checkout attempt — echoed back so the
   * SPA/QA can correlate a session with `Subscription.providerRef`. */
  providerRef: z.string().min(1),
});
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;
